<template>
  <div class="user-input-container"
       :style="[
        CssStyleHelpers.paddings(cardsGroup?.cardsOuterSpacing || 0)
      ]">
    <div class="card-container">
      <div
           :class="['card-content', {'p-4': hasPadding, 'rounded-lg': hasRounded, 'bg-white': hasWhiteBackground, 'card-shadow': hasCardShadow}]">
        <textarea 
          ref="textareaRef"
          v-model="userInput"
          @input="handleInputChange"
          class="user-input"
          :placeholder="'Введіть відповідь'"
          rows="2" />

        <div v-if="showAnswers && trueAnswer"
             class="user-input-footer border-t border-gray-200 pt-2">
          <div class="true-answer">{{ trueAnswer }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {ref, watch, computed, onMounted, nextTick} from 'vue';
import {CssStyleHelpers} from "../../utils/cssStyleHelpers";

const props = defineProps<{
  cardsGroup: any;
  mainGroup: any;
  trueAnswers?: string[];
  userAnswers?: string[];
  showAnswers?: boolean;
}>();

const emit = defineEmits<{
  'update:userAnswers': [answers: string[]];
}>();

const userInput = ref<string>('');
const textareaRef = ref<HTMLTextAreaElement | null>(null);

// Функция для автоматического изменения высоты textarea
function autoResizeTextarea() {
  if (!textareaRef.value) return;
  const textarea = textareaRef.value;
  // Сбрасываем высоту, чтобы получить правильный scrollHeight
  textarea.style.height = 'auto';
  // Устанавливаем новую высоту на основе содержимого
  const newHeight = Math.max(80, textarea.scrollHeight);
  textarea.style.height = `${newHeight}px`;
}

onMounted(() => {
  // Заполняем из userAnswers если есть
  if (props.userAnswers && props.userAnswers.length > 0) {
    userInput.value = props.userAnswers[0] || '';
  }
  // Подстраиваем высоту textarea после монтирования
  nextTick(() => {
    autoResizeTextarea();
  });
});

const hasShadow = computed(() => {
  return props.cardsGroup?.isShowShadow === true;
});

const hasPadding = computed(() => {
  return props.cardsGroup?.isShowCardPadding !== false;
});

const hasRounded = computed(() => {
  return props.cardsGroup?.isShowCardRounded !== false;
});

const hasWhiteBackground = computed(() => {
  return hasShadow.value && props.cardsGroup?.isShowCardWhiteBackground !== false;
});

const hasCardShadow = computed(() => {
  return hasShadow.value;
});

const trueAnswer = computed(() => {
  if (props.trueAnswers && props.trueAnswers.length > 0) {
    return props.trueAnswers[0];
  }
  return '';
});

// Слідкуємо за зовнішніми змінами props.userAnswers.
// Порожнє/відсутнє зовнішнє значення НЕ стирає вже показану відповідь: батьківський
// компонент може перерендерити елемент без exposedData.userAnswers (напр. у кабінеті
// вчителя після збереження коментаря) — відповідь учня має лишитись у полі.
watch(() => props.userAnswers, (newAnswers) => {
  const incoming = newAnswers?.[0] ?? '';

  if (incoming === userInput.value) return;
  if (!incoming && userInput.value) return;

  userInput.value = incoming;
  // Подстраиваем высоту textarea после обновления
  nextTick(() => {
    autoResizeTextarea();
  });
}, {deep: true});

function handleInputChange() {
  // Автоматически подстраиваем высоту
  autoResizeTextarea();
  // Эмитим обновленные ответы как массив с одним элементом
  emit('update:userAnswers', [userInput.value]);
}
</script>

<style scoped>
.user-input-container {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.card-container {
  display: flex;
  flex-direction: column;
  margin-bottom: 0px;
  border: 0px solid transparent;
  transition: all 0.2s ease;
  outline: solid 1px transparent;
}

.card-content {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.user-input {
  width: 100%;
  padding: 8px 12px;
  transition: all 0.2s ease;
  border: 1px solid var(--color-border);
  border-radius: 10px;
  outline: none;
  resize: none;
  min-height: 80px;
  font-family: inherit;
  font-size: var(--vll-font-size, 19px);
  cursor: text;
  color: var(--text-primary);
  overflow: hidden;
}

.user-input:focus {
  border: solid 1px var(--color-primary);
  cursor: text;
}

.user-input:readonly {
  background-color: #f9fafb;
  cursor: not-allowed;
}

/* Тінь як у MultipleChoiceTest */
.card-content.card-shadow {
  box-shadow: rgba(99, 99, 99, 0.2) 0px 2px 8px 0px;
}

.true-answer {
  color: var(--text-primary);
  border-left: solid 5px rgba(0, 191, 127, 1);
  padding: 10px;
  border-radius: 8px;
  font-size: var(--vll-font-size, 19px);
}
</style>
